import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownSection,
  DropdownTrigger,
  SharedSelection,
  Spinner,
  Switch
} from '@heroui/react';
import { NavArrowDown } from 'iconoir-react';
import { useQueryState, parseAsString } from 'nuqs';

import i18n from '~/common/helpers/i18n';

type DefaultRefreshInterval = `${number}` | 'off';

export type RefreshIntervalOption = {
  value: DefaultRefreshInterval;
  label: string;
  shortLabel?: string;
};

type RefreshIntervalSelectorProps = {
  onChange?: (interval: DefaultRefreshInterval) => void;
  intervals?: RefreshIntervalOption[];
  defaultInterval?: DefaultRefreshInterval;
};

export function RefreshIntervalSelector({
  onChange,
  intervals = [
    { value: '5', label: '5 seconds', shortLabel: '5s' },
    { value: '10', label: '10 seconds', shortLabel: '10s' },
    { value: '30', label: '30 seconds', shortLabel: '30s' }
  ],
  defaultInterval
}: RefreshIntervalSelectorProps) {
  const [refreshInterval, setRefreshInterval] = useQueryState(
    'refreshInterval',
    parseAsString.withDefault(defaultInterval || intervals[0].value)
  );

  const handleIntervalChange = (keys: SharedSelection) => {
    const selectedKey = Array.from(keys)[0];
    if (!selectedKey) return;

    if (selectedKey === 'auto-refresh') {
      if (refreshInterval === 'off') {
        setRefreshInterval(intervals[0].value);
        onChange?.(intervals[0].value);
      } else {
        setRefreshInterval('off');
        onChange?.('off');
      }
    } else {
      setRefreshInterval(selectedKey as DefaultRefreshInterval);
      onChange?.(selectedKey as DefaultRefreshInterval);
    }
  };

  return (
    <div className='flex gap-1'>
      <div className='flex items-center gap-2'>
        {refreshInterval !== 'off' && (
          <span className='text-sm text-on-surface-variant'>
            {i18n.t('scenes.runtime.labels.refresh_data_every')}
          </span>
        )}
        <Dropdown
          placement='bottom-start'
          className='rounded-md bg-white shadow-xl'
        >
          <DropdownTrigger>
            <Button
              variant='solid'
              size='sm'
              radius='full'
              aria-label='Refresh interval'
              className='h-6 min-w-0 gap-0.5 px-2 font-medium'
              endContent={<NavArrowDown className='h-4 w-4' />}
            >
              {refreshInterval !== 'off'
                ? intervals.find(i => i.value === refreshInterval)
                    ?.shortLabel || `${refreshInterval}s`
                : i18n.t('scenes.runtime.labels.auto_refresh_off')}
            </Button>
          </DropdownTrigger>
          <DropdownMenu
            aria-label='Refresh interval selector'
            selectedKeys={refreshInterval ? [refreshInterval] : []}
            selectionMode='single'
            onSelectionChange={handleIntervalChange}
          >
            <DropdownSection showDivider className='text-sm'>
              {intervals.map(i => (
                <DropdownItem
                  className='!text-label-lg text-on-surface-variant aria-checked:text-on-surface'
                  key={i.value}
                >
                  {i.label}
                </DropdownItem>
              ))}
            </DropdownSection>
            <DropdownSection className='mb-0'>
              <DropdownItem
                classNames={{
                  selectedIcon: 'hidden'
                }}
                key='auto-refresh'
              >
                <div className='flex w-full items-center justify-between gap-5'>
                  <span className='font-medium'>
                    {i18n.t('scenes.runtime.labels.auto_refresh')}
                  </span>
                  <Switch
                    isSelected={refreshInterval !== 'off'}
                    onChange={() => {
                      if (refreshInterval === 'off') {
                        handleIntervalChange(new Set([intervals[0].value]));
                      } else {
                        handleIntervalChange(new Set(['off']));
                      }
                    }}
                  />
                </div>
              </DropdownItem>
            </DropdownSection>
          </DropdownMenu>
        </Dropdown>
      </div>
      {refreshInterval !== 'off' && (
        <Spinner
          variant='dots'
          className='size-6 [&>div]:mb-1 [&>div]:size-6 [&_i]:size-[4px]'
        />
      )}
    </div>
  );
}

export type { RefreshIntervalSelectorProps, DefaultRefreshInterval };
